import React from 'react';
import './BankSyncModal.css';
import './BankSyncAllModal.css';

// Fragt Konten sequentiell ab (nicht parallel), da jede Bank ein eigenes PIN
// und ggf. eine eigene TAN braucht - das muss der Nutzer pro Konto einzeln
// eingeben. PIN/TAN leben wie bei BankSyncModal nur lokal in diesem State.
export default function BankSyncAllModal(props) {

	const x = props.x;

	const [accounts, setAccounts] = React.useState(null);
	const [currentIndex, setCurrentIndex] = React.useState(0);
	const [pin, setPin] = React.useState('');
	const [tanCode, setTanCode] = React.useState('');
	const [fintsState, setFintsState] = React.useState('');
	const [challenge, setChallenge] = React.useState(null);
	const [busy, setBusy] = React.useState(false);
	const [error, setError] = React.useState('');
	const [hint, setHint] = React.useState('');
	const [remember, setRemember] = React.useState(false);
	// null = Schalterzustand noch nicht geladen; solange startet kein Auto-Abruf.
	const [pinUse, setPinUse] = React.useState(null);
	const [usingStored, setUsingStored] = React.useState(false);

	React.useEffect(() => {
		x.was.axGet('Backend/webapp.php?module=Banking&task=BankingConnectionList', (result) => {
			if (result.data.returncode === 1) {
				setAccounts(result.data.content
					.filter((c) => parseInt(c.disabled, 10) !== 1)
					.map((c) => ({
						connectionId: c.connectionid,
						label: c.bankname + ' - ' + c.accountlabel,
						pinstored: parseInt(c.pinstored, 10) === 1,
						storedTried: false,
						status: 'pending',
						message: '',
					})));
			}
		});
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingPinSwitch', {}, (result) => {
			setPinUse(result.data.returncode === 1 && parseInt(result.data.content.enabled, 10) === 1);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const current = (accounts && currentIndex < accounts.length) ? accounts[currentIndex] : null;
	const allDone = accounts !== null && currentIndex >= accounts.length;

	// Hat das aktuelle Konto einen gemerkten PIN, ohne Nachfrage lossynchronisieren.
	// storedTried verhindert eine Endlosschleife, falls der Versuch scheitert.
	React.useEffect(() => {
		if (pinUse !== true || !current || busy || challenge !== null) return;
		if (current.pinstored && !current.storedTried && current.status === 'pending') {
			updateCurrent({ storedTried: true });
			setUsingStored(true);
			setBusy(true);
			x.was.axPost('Backend/webapp.php?module=Banking&task=BankingSync', {
				connectionId: current.connectionId,
				pin: '',
			}, handleResult);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pinUse, accounts, currentIndex, busy, challenge]);

	function updateCurrent(patch) {
		setAccounts((list) => list.map((a, i) => (i === currentIndex ? { ...a, ...patch } : a)));
	}

	function advance() {
		setPin('');
		setTanCode('');
		setFintsState('');
		setChallenge(null);
		setError('');
		setHint('');
		setRemember(false);
		setUsingStored(false);
		setCurrentIndex((i) => i + 1);
	}

	function skipCurrent() {
		updateCurrent({ status: 'skipped' });
		advance();
	}

	function submitPin() {
		if (pin === '' || !current) return;
		setBusy(true);
		setError('');
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingSync', {
			connectionId: current.connectionId,
			pin: pin,
			rememberPin: remember ? 1 : 0,
		}, handleResult);
	}

	function submitTan() {
		if (tanCode === '' || !current) return;
		setBusy(true);
		setError('');
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingSync', {
			connectionId: current.connectionId,
			pin: pin,
			rememberPin: (pin !== '' && remember) ? 1 : 0,
			tanCode: tanCode,
			fintsState: fintsState,
		}, handleResult);
	}

	function handleResult(result) {
		setBusy(false);
		if (result.data.returncode !== 1) {
			const content = result.data.content || {};
			// Gemerkter PIN wurde abgelehnt (und serverseitig verworfen) - fuer
			// dieses Konto auf manuelle Eingabe zurueckfallen statt es als
			// fehlgeschlagen abzuhaken.
			if (content.storedPinCleared || content.status === 'pin-required') {
				setUsingStored(false);
				updateCurrent({ pinstored: false });
				setHint(content.storedPinCleared ? 'Der gemerkte PIN wurde abgelehnt und verworfen – bitte neu eingeben.' : '');
				return;
			}
			const message = content.message || 'Abruf fehlgeschlagen.';
			updateCurrent({ status: 'error', message });
			advance();
			return;
		}
		const content = result.data.content;
		if (content.status === 'tan-required') {
			updateCurrent({ status: 'tan-required' });
			setChallenge({ text: content.challenge, tanMediumName: content.tanMediumName });
			setFintsState(content.fintsState);
			setTanCode('');
			return;
		}
		updateCurrent({ status: 'success', message: content.transactionCount + ' neue Umsätze' });
		advance();
	}

	function close() {
		setPin('');
		setTanCode('');
		setFintsState('');
		props.onClose();
	}

	return (
		<div className="banking-sync-overlay">
			<div className="banking-sync-modal banking-sync-all-modal">
				<div className="banking-sync-header">
					<span>Umsätze aller Konten abrufen</span>
					<i className="bi bi-x-lg table-icon" onClick={close}></i>
				</div>
				<div className="banking-sync-body">
					{accounts === null &&
						<p>Lade Konten…</p>
					}
					{accounts !== null && accounts.length === 0 &&
						<p>Keine aktiven Bankverbindungen vorhanden.</p>
					}
					{accounts !== null && accounts.length > 0 &&
						<ul className="banking-sync-all-list">
							{accounts.map((a, i) => (
								<li key={a.connectionId} className={`banking-sync-all-item status-${a.status}`}>
									<span className="banking-sync-all-label">{a.label}</span>
									<span className="banking-sync-all-status">
										{a.status === 'success' && <><i className="bi bi-check-circle-fill"></i> {a.message}</>}
										{a.status === 'error' && <><i className="bi bi-exclamation-triangle-fill"></i> {a.message}</>}
										{a.status === 'skipped' && 'übersprungen'}
										{a.status === 'tan-required' && 'TAN erforderlich…'}
										{a.status === 'pending' && i === currentIndex && 'läuft…'}
									</span>
								</li>
							))}
						</ul>
					}

					{current && challenge === null && usingStored &&
						<div className="banking-sync-all-active">
							<p><i className="bi bi-key"></i> Verwende gemerkten PIN für {current.label}…</p>
						</div>
					}

					{current && challenge === null && !usingStored &&
						<div className="banking-sync-all-active">
							{hint !== '' && <p className="banking-sync-hint"><i className="bi bi-info-circle"></i> {hint}</p>}
							<label htmlFor="banking-sync-all-pin">PIN für {current.label}</label>
							<input
								id="banking-sync-all-pin"
								className="brokerage-pin-input"
								type="text"
								inputMode="numeric"
								autoComplete="one-time-code"
								value={pin}
								onChange={(e) => setPin(e.target.value)}
								onKeyDown={(e) => { if (e.key === 'Enter') submitPin(); }}
								autoFocus
							/>
							{pinUse === true &&
								<label className="banking-sync-remember">
									<input
										type="checkbox"
										checked={remember}
										onChange={(e) => setRemember(e.target.checked)}
									/>
									PIN merken (verschlüsselt, nur dieser Browser)
								</label>
							}
							{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
							<div className="banking-sync-all-actions">
								<button type="button" className="btn" disabled={busy || pin === ''} onClick={submitPin}>
									{busy ? 'Verbinde…' : 'Abrufen'}
								</button>
								<button type="button" className="btn" disabled={busy} onClick={skipCurrent}>
									Überspringen
								</button>
							</div>
						</div>
					}

					{current && challenge !== null &&
						<div className="banking-sync-all-active">
							{challenge.text && <p className="banking-sync-challenge">{challenge.text}</p>}
							{challenge.tanMediumName && <p className="banking-sync-medium">Gerät: {challenge.tanMediumName}</p>}
							<label htmlFor="banking-sync-all-tan">TAN für {current.label}</label>
							<input
								id="banking-sync-all-tan"
								type="text"
								autoComplete="off"
								value={tanCode}
								onChange={(e) => setTanCode(e.target.value)}
								onKeyDown={(e) => { if (e.key === 'Enter') submitTan(); }}
								autoFocus
							/>
							<div className="banking-sync-all-actions">
								<button type="button" className="btn" disabled={busy || tanCode === ''} onClick={submitTan}>
									{busy ? 'Bestätige…' : 'TAN bestätigen'}
								</button>
							</div>
						</div>
					}

					{allDone &&
						<div className="banking-sync-all-actions">
							<button type="button" className="btn" onClick={props.onSuccess}>Fertig</button>
						</div>
					}
				</div>
			</div>
		</div>
	)

};
